import React, { useRef } from "react"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faShop } from "@fortawesome/pro-light-svg-icons"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { SUPPLIER_ADMIN_ROUTE, SUPPLIER_ADMIN_TERMS_AND_CONDITIONS_ROUTE } from "../../../../../../constants/routes"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { enqueueSnackbar } from "notistack"
import { useNavigate } from "react-router"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectEffectiveCompanyId } from "../../../../../context/contextSelectors"
import { createTermsAndCondition, TermsAndConditionQuery } from "../termsAndConditionApi"
import { TermsAndConditionsFormValues } from "../termsAndConditionTypes"
import { FormikProps } from "formik"
import { CreateOrEditTermsAndConditionForm } from "../components/CreateEditTermsAndConditionForm"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"

export function CreateTermsAndConditions() {
    const formRef = useRef<FormikProps<TermsAndConditionsFormValues>>(null)

    const navigate = useNavigate()
    const supplierId = useAppSelector(selectEffectiveCompanyId)

    const { mutate: callCreateTermsAndConditions } = useApiMutation(createTermsAndCondition, {
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [TermsAndConditionQuery.LIST] })
            navigate(SUPPLIER_ADMIN_TERMS_AND_CONDITIONS_ROUTE)
            if (data?.serviceResponse?.displayMessage) {
                enqueueSnackbar({
                    variant: "success",
                    message: data?.serviceResponse?.displayMessage,
                })
            }
        },
    })

    const handleSaveClick = () => {
        formRef.current?.submitForm()
    }

    const submitForm = (value: TermsAndConditionsFormValues) => {
        if (!supplierId) throw new Error("supplierId is required")
        if (!value.type) throw new Error("type is required")

        callCreateTermsAndConditions({
            callName: value.callName,
            languageCode: value.languageCode,
            text: value.text,
            type: value.type,
            isActive: value.isActive,
            supplier: {
                id: supplierId,
            },
        })
    }

    return (
        <>
            <LocalizedStrict id="supplier-admin-settings-terms-and-conditions" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SUPPLIER_TERMS_AND_CONDITIONS_CREATE}
                    title="Terms and conditions"
                    icon={<FontAwesomeIcon icon={faShop} />}
                    rightElement={
                        <LocalizedStrict id="supplier-admin-settings-terms-and-conditions-create-button">
                            <StandardButton onClick={handleSaveClick}>Create</StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="supplier-nav-item-home">
                            <BreadcrumbLink to={SUPPLIER_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-admin-nav-item-settings">
                            <Breadcrumb>Settings</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-admin-nav-item-terms-and-conditions">
                            <BreadcrumbLink to={SUPPLIER_ADMIN_TERMS_AND_CONDITIONS_ROUTE}>
                                Terms and conditions
                            </BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-admin-nav-item-create-terms-and-conditions">
                            <Breadcrumb>Create terms and conditions</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <CreateOrEditTermsAndConditionForm ref={formRef} onSubmit={submitForm} />
        </>
    )
}
